/**
 * QuickBytesGrid - bento-style concept grid for the Quick Bytes page.
 *
 * Pulls the highlights feed via `useQuickBytesHighlights` (infinite
 * query, 60 carousels per page), assigns mixed tile sizes (regular /
 * tall / large) over the cumulative loaded list, and opens a
 * `QuickBytesCarouselModal` when a tile is clicked. An
 * IntersectionObserver sentinel at the bottom of the grid auto-loads
 * the next page when it enters the viewport. The modal receives the
 * same flat list plus `fetchNextPage` / `hasNextPage` so cross-concept
 * navigation in the modal can prefetch/extend the deck too.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickBytesHighlights } from '@/hooks/mindspace/useRevision';
import {
  countActiveFilters,
  useTagFilters,
} from '@/hooks/mindspace/useTagFilters';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import type { HighlightsFeedCarousel } from '@/lib/api-client';
import { resolveTileSizes } from '@/lib/mindspace/bandPattern';
import { QuickBytesCarouselModal } from '@/components/features/mindspace/quick-bytes/QuickBytesCarouselModal';
import { QuickBytesTile } from '@/components/features/mindspace/quick-bytes/QuickBytesTile';
import {
  QuickBytesEmptyState,
  QuickBytesFeedFooter,
  QuickBytesGridSkeleton,
} from '@/components/features/mindspace/quick-bytes/QuickBytesGridStates';

export function QuickBytesGrid() {
  const navigate = useNavigate();
  const filterState = useTagFilters();
  const { asHighlightsFilters, hasAnyFilter, setFilters, bookmarked } =
    filterState;
  const filters = asHighlightsFilters();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useQuickBytesHighlights(filters);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A phone draws every tile one size, with a caption (`QuickBytesTile`'s
  // `compact`): two columns leave no room for the bento's 2x2 and 1x2.
  const compact = useMediaQuery('(max-width: 767.98px)');

  // Flatten pages into one carousel list. Memoized so size-resolution
  // and the modal don't re-run on every render - only when `data.pages`
  // identity changes.
  const carousels = useMemo<HighlightsFeedCarousel[]>(
    () => data?.pages.flatMap(p => p.carousels) ?? [],
    [data?.pages]
  );
  const sizes = useMemo(() => resolveTileSizes(carousels), [carousels]);
  const total = data?.pages[0]?.total ?? carousels.length;

  // IntersectionObserver sentinel at the bottom of the grid - triggers
  // the next-page fetch when it scrolls into view. Disabled while a
  // page is already in flight or when no more pages remain.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: '400px' } // pre-fetch when within 400px of the bottom
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, carousels.length]);

  if (isLoading) return <QuickBytesGridSkeleton />;

  if (carousels.length === 0) {
    return (
      <QuickBytesEmptyState
        hasFilters={hasAnyFilter}
        bookmarksOnly={bookmarked && countActiveFilters(filterState) === 0}
        onShowAll={() => setFilters({ bookmarked: false })}
        onGenerate={() => navigate('/')}
        onClear={() =>
          setFilters({
            domain: [],
            user: [],
            match: 'any',
            q: '',
            lessons: [],
            documents: [],
            timeAxis: 'last_reviewed_at',
            window: 'all',
            dueToday: false,
            bookmarked: false,
          })
        }
      />
    );
  }

  return (
    <>
      <div className='min-h-0 flex-1 overflow-y-auto p-4'>
        {/* Inner flex column with `min-h-full` so when the grid is
            shorter than the viewport (e.g. just a few tiles), `mt-auto`
            on the sentinel pushes the "reached the end" footer to the
            bottom of the container instead of letting it float just
            below the grid. */}
        <div className='flex min-h-full flex-col'>
          <div className='grid grid-flow-row-dense grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4'>
            {carousels.map((carousel, idx) => (
              <QuickBytesTile
                key={`${carousel.concept_id}-${carousel.source_lesson_id ?? ''}-${idx}`}
                carousel={carousel}
                size={compact ? 'regular' : sizes[idx]}
                compact={compact}
                onClick={() => setSelectedId(carousel.concept_id)}
              />
            ))}
          </div>

          {/* Sentinel + footer status. Sentinel stays in the DOM even
              when there's no next page so the observer can re-attach
              cleanly when filters change. `mt-auto` pins it to the
              bottom when content doesn't fill the viewport. */}
          <div
            ref={sentinelRef}
            className='mt-auto flex items-center justify-center pb-2 pt-6'
          >
            <QuickBytesFeedFooter
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={!!hasNextPage}
              loaded={carousels.length}
              total={total}
            />
          </div>
        </div>
      </div>

      <QuickBytesCarouselModal
        carousels={carousels}
        initialConceptId={selectedId}
        onClose={() => setSelectedId(null)}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
}
